import { cn } from '@dz-web/esboot-browser';
import { useState } from 'react';

import { tryGtag } from '@/helpers/gtags';
import { useSectionNav } from '@/hooks/hooks/use-section-nav';

import { BuyPopup } from './components/buy-popup/buy-popup';
import { CompanyIntro } from './components/company-intro/company-intro';
import { CustomerServiceFooter } from './components/customer-service-footer/customer-service-footer';
import { ProductIntroList } from './components/product-intro-list/product-intro-list';
import { SwiperList } from './components/swiper-list/swiper-list';
import { VersionCompareImageList } from './components/version-compare-image-list/version-compare-image-list';
import { VersionCompareTable } from './components/version-compare-table/version-compare-table';
import { PackageEnum } from './constants/enum';
import { usePackages } from './hooks/use-packages';
import headerLogo from './images/header-logo.png';
import './index.scss';

const headerIds = {
  productFeatures: 'product-features',
  versionComparison: 'version-comparison',
  companyQualifications: 'company-qualifications',
  customerService: 'customer-service',
} as const;

// 导航菜单项与对应的 section id 映射
const navSections = [
  { id: headerIds.versionComparison, label: '版本對比' },
  { id: headerIds.productFeatures, label: '產品特色' },
  { id: headerIds.companyQualifications, label: '公司資質' },
  { id: headerIds.customerService, label: '聯繫與支持' },
] as const;

const AppHome: React.FC = () => {
  const [buyPopupVisible, setBuyPopupVisible] = useState(false);
  const { packages, packagesMap } = usePackages();
  const [selectPackage, setSelectPackage] = useState(() => packagesMap[PackageEnum.LITE]);
  const [buyPackageType, setBuyPackageType] = useState<PackageEnum>(() => PackageEnum.LITE);

  const handlePurchase = () => {
    // gtag 跟踪 创建订单
    tryGtag('event', 'conversion', {
      send_to: window.GLOBAL_CONFIG.mobileShop.gtag.clickBuy,
    });

    setBuyPopupVisible(true);
  };

  const { activeNavId, scrollToId } = useSectionNav({
    sections: navSections,
    defaultActiveId: headerIds.versionComparison,
  });

  return (
    <>
      <div styleName="page">
        <header styleName="header" id="nav-header">
          <a href="https://www.megahubhk.com" target="_blank" rel="noreferrer">
            <img
              styleName="header-logo"
              src={headerLogo}
              alt="header-logo"
              onClick={() => window.scrollTo({ top: 0 })}
            />
          </a>
          <div styleName="nav-menu">
            {navSections.map((section) => (
              <div
                key={section.id}
                styleName={cn('nav-menu-item', { active: activeNavId === section.id })}
                onClick={() => scrollToId(section.id)}
              >
                {section.label}
              </div>
            ))}
          </div>
        </header>

        <SwiperList packageType={selectPackage.type} />

        <div styleName="padding-side">
          <h2 styleName="h2 version-select-title">版本選擇</h2>
          <div styleName="button-group">
            {packages.map((p) => (
              <button
                type="button"
                key={p.code}
                styleName={cn({
                  active: p.code === selectPackage?.code,
                })}
                onClick={() => {
                  setSelectPackage(p);
                  setBuyPackageType(p.type);
                }}
              >
                {p.name}
              </button>
            ))}
          </div>
          <h2 styleName="h2" id={headerIds.versionComparison}>
            版本對比
          </h2>
          <VersionCompareTable
            onPurchase={(packageType) => {
              setBuyPackageType(packageType);
              handlePurchase();
            }}
          />
          <h2 styleName="h2" id={headerIds.productFeatures}>
            產品特色
          </h2>
          <ProductIntroList />
        </div>
        <VersionCompareImageList />
        <div styleName="padding-side">
          <h2 styleName="h2" id={headerIds.companyQualifications}>
            公司資質
          </h2>
          <CompanyIntro />
        </div>
        <CustomerServiceFooter id={headerIds.customerService} />
      </div>
      <div styleName="float-bottom-bar">
        <div styleName="max-width-container">
          <div styleName="warning-content">
            <div styleName="order-title">
              即時開通,單月訂購<span styleName="order-price">${selectPackage.price}</span>
              <span styleName="order-price-unit">HKD</span>
            </div>
            <div styleName="order-tips">
              <div styleName="order-sub-title">*此為單月方案，到期後如需繼續使用，請聯繫客服續費。</div>
              <div>*賬號由系統自動生成，支付成功後，系統將自動發送賬號信息到您的PayPal郵箱和短信</div>
            </div>
          </div>
          <button type="button" styleName="purchase-btn" onClick={handlePurchase}>
            立即訂購
          </button>
        </div>
      </div>
      <BuyPopup
        visible={buyPopupVisible}
        onClose={() => setBuyPopupVisible(false)}
        defaultPackageType={buyPackageType}
      />
    </>
  );
};

export default AppHome;
